// Catálogo de variables disponibles para condiciones del bot.
// Las custom (campos de la tarjeta del cliente) se autodetectan en runtime.

export const VARIABLE_GROUPS = [
  {
    label: '⚙️ Sistema',
    items: [
      { key: '__manual__', label: 'Entrada manual', type: 'value', desc: 'Escribir un valor literal' },
      { key: '__empty__', label: 'Vacío', type: 'value' },
    ],
  },
  {
    label: '💬 Mensaje recibido',
    items: [
      { key: 'message.text', label: 'Mensaje del cliente', type: 'text' },
      { key: 'message.type', label: 'Tipo de mensaje', type: 'enum', options: ['text', 'audio', 'image', 'video', 'document'] },
      { key: 'message.channel', label: 'Canal del mensaje', type: 'enum', options: ['whatsapp', 'instagram', 'facebook', 'tiktok', 'email'] },
    ],
  },
  {
    label: '👤 Contacto',
    items: [
      { key: 'contact.name', label: 'Contacto: Nombre', type: 'text' },
      { key: 'contact.phone_number', label: 'Contacto: Teléfono', type: 'text' },
      { key: 'contact.email', label: 'Contacto: Correo', type: 'text' },
      { key: 'contact.identifier', label: 'Contacto: Identificador', type: 'text' },
      { key: 'contact.labels', label: 'Contacto: Etiquetas', type: 'array' },
      { key: 'contact.days_since_first_message', label: 'Contacto: Días desde primer mensaje', type: 'number' },
    ],
  },
  {
    label: '🎯 Lead',
    items: [
      { key: 'deal.id', label: 'Lead: Número', type: 'number' },
      { key: 'deal.title', label: 'Lead: Título', type: 'text' },
      { key: 'deal.value', label: 'Lead: Presupuesto', type: 'number' },
      { key: 'deal.currency', label: 'Lead: Moneda', type: 'text' },
      { key: 'deal.assignee_name', label: 'Lead: Responsable', type: 'text' },
      { key: 'deal.pipeline_name', label: 'Lead: Embudo', type: 'text' },
      { key: 'deal.stage_name', label: 'Lead: Etapa actual', type: 'text' },
      { key: 'deal.days_in_stage', label: 'Lead: Días en etapa', type: 'number' },
      { key: 'deal.created_at', label: 'Lead: Fecha de creación', type: 'date' },
    ],
  },
  {
    label: '💭 Conversación',
    items: [
      { key: 'conversation.id', label: 'Conversación: ID', type: 'number' },
      { key: 'conversation.status', label: 'Conversación: Estado', type: 'enum', options: ['open', 'resolved', 'pending', 'snoozed'] },
      { key: 'conversation.inbox_name', label: 'Conversación: Bandeja', type: 'text' },
      { key: 'conversation.labels', label: 'Conversación: Etiquetas (sucursal)', type: 'array' },
      { key: 'conversation.branch', label: 'Conversación: Sucursal del número', type: 'text' },
      { key: 'conversation.messages_count', label: 'Conversación: Cantidad mensajes', type: 'number' },
      { key: 'conversation.last_activity_at', label: 'Conversación: Última actividad', type: 'date' },
    ],
  },
];

export const OPERATORS_BY_TYPE = {
  text: [
    { key: 'equals', label: 'Es igual a' },
    { key: 'not_equals', label: 'No es igual a' },
    { key: 'contains', label: 'Contiene' },
    { key: 'not_contains', label: 'No contiene' },
    { key: 'starts_with', label: 'Empieza con' },
    { key: 'ends_with', label: 'Termina con' },
    { key: 'present', label: 'Tiene valor' },
    { key: 'blank', label: 'Está vacío' },
  ],
  number: [
    { key: 'equals', label: 'Igual a (=)' },
    { key: 'not_equals', label: 'Distinto de (≠)' },
    { key: 'greater_than', label: 'Mayor que (>)' },
    { key: 'less_than', label: 'Menor que (<)' },
    { key: 'greater_or_equal', label: 'Mayor o igual (≥)' },
    { key: 'less_or_equal', label: 'Menor o igual (≤)' },
    { key: 'between', label: 'Entre (rango)' },
    { key: 'present', label: 'Tiene valor' },
    { key: 'blank', label: 'Está vacío' },
  ],
  date: [
    { key: 'is_today', label: 'Es hoy' },
    { key: 'is_this_week', label: 'Es esta semana' },
    { key: 'is_this_month', label: 'Es este mes' },
    { key: 'before', label: 'Es antes de' },
    { key: 'after', label: 'Es después de' },
    { key: 'days_ago', label: 'Hace X días' },
    { key: 'present', label: 'Tiene valor' },
    { key: 'blank', label: 'Está vacío' },
  ],
  array: [
    { key: 'contains', label: 'Contiene' },
    { key: 'not_contains', label: 'No contiene' },
    { key: 'present', label: 'Tiene valor' },
    { key: 'blank', label: 'Está vacío' },
  ],
  enum: [
    { key: 'equals', label: 'Es igual a' },
    { key: 'not_equals', label: 'No es igual a' },
  ],
  value: [],
};

export const OPERATORS_NEEDING_VALUE = ['equals', 'not_equals', 'contains', 'not_contains', 'starts_with', 'ends_with', 'greater_than', 'less_than', 'greater_or_equal', 'less_or_equal', 'between', 'before', 'after', 'days_ago'];

export function findVariable(key, customDefs = []) {
  if (!key) return null;
  for (const group of VARIABLE_GROUPS) {
    const found = group.items.find(i => i.key === key);
    if (found) return found;
  }
  // Custom fields: 'custom.{attribute_key}'
  if (key.startsWith('custom.')) {
    const attrKey = key.replace('custom.', '');
    const def = customDefs.find(d => d.attribute_key === attrKey);
    if (def) {
      return {
        key,
        label: `Lead: ${def.attribute_display_name}`,
        type: mapCustomTypeToVariableType(def.attribute_display_type),
      };
    }
  }
  return null;
}

function mapCustomTypeToVariableType(displayType) {
  if (['number', 'currency', 'percent', 'rating'].includes(displayType)) return 'number';
  if (['date', 'datetime', 'time'].includes(displayType)) return 'date';
  if (['list', 'multiselect', 'checkbox'].includes(displayType)) return 'enum';
  return 'text';
}

export function customGroupForDefinitions(customDefs = []) {
  if (!customDefs.length) return null;
  return {
    label: '🎨 Campos personalizados (de tu tarjeta)',
    items: customDefs.map(d => ({
      key: `custom.${d.attribute_key}`,
      label: `Lead: ${d.attribute_display_name}`,
      type: mapCustomTypeToVariableType(d.attribute_display_type),
    })),
  };
}
